/* eslint-disable @next/next/no-img-element */
import { unit, C, Canvas, Abs, Card, Avatar, Icon, Sidebar, Arrows } from './kit';

// Vector rebuild of /img/redesign/native-visual-v2.png (1448×1086 canvas).
const W = 1448;
const H = 1086;
const u = unit(W);

const Title = ({ children, info }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: u(7), fontSize: u(14.5), fontWeight: 700, color: C.ink }}>
    {children}
    {info && <Icon u={u} name="info" size={14} color="#9C98B0" stroke={2} />}
  </div>
);

const More = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: u(8), fontSize: u(13), fontWeight: 600, color: C.blue }}>
    {children} <Icon u={u} name="arrowRight" size={14} stroke={2.2} />
  </div>
);

const Panel = ({ x, y, w, h, children, style }) => (
  <Abs u={u} x={x} y={y} w={w} h={h} style={{
    background: '#fff', borderRadius: u(10), border: `${u(1.5)} solid #F0EEF5`,
    boxSizing: 'border-box', ...style,
  }}>
    {children}
  </Abs>
);

const Kpi = ({ x, w, label, value, delta, up, link }) => (
  <Panel x={x} y={138} w={w} h={104}>
    <Abs u={u} x={19} y={20} style={{ fontSize: u(14), fontWeight: 600 }}>{label}</Abs>
    <Abs u={u} x={19} y={50} style={{ fontSize: u(29), fontWeight: 700, letterSpacing: '-0.01em' }}>{value}</Abs>
    {delta && (
      <Abs u={u} x={up ? 125 : 182} y={63} style={{
        display: 'flex', alignItems: 'center', gap: u(4), fontSize: u(14), fontWeight: 600,
        color: up ? C.green : C.red,
      }}>
        <Icon u={u} name={up ? 'arrowUp' : 'arrowDown'} size={15} stroke={2.4} /> {delta}
      </Abs>
    )}
    {link && <Abs u={u} x={w - 72} y={66} style={{ fontSize: u(13), fontWeight: 600, color: C.blue }}>{link}</Abs>}
  </Panel>
);

const Chip = ({ x, y, w = 44, label, bg, color }) => (
  <Abs u={u} x={x - w / 2} y={y - 12} w={w} h={24} style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: bg, color, borderRadius: u(5), fontSize: u(11), fontWeight: 600,
  }}>{label}</Abs>
);

const Status = ({ label, bg, color }) => (
  <span style={{ padding: `${u(3)} ${u(5)}`, borderRadius: u(4), background: bg, color, fontSize: u(10.5), fontWeight: 600 }}>{label}</span>
);

const DEPTS = [
  { name: 'Engineering', value: '180 (35%)', pct: 35, color: '#2BB55A' },
  { name: 'Product', value: '120 (23%)', pct: 23, color: '#6A4CF5' },
  { name: 'Marketing', value: '80 (16%)', pct: 16, color: '#B8B3E0' },
  { name: 'Sales', value: '70 (14%)', pct: 14, color: '#F7B648' },
  { name: 'People Ops', value: '62 (12%)', pct: 12, color: '#EF4B33' },
];
// Clockwise order around the donut, as in the original render.
const RING = ['Engineering', 'People Ops', 'Sales', 'Product', 'Marketing'].map(n => DEPTS.find(d => d.name === n));
const donutGradient = (() => {
  let acc = 0;
  const stops = RING.map(d => {
    const from = acc; acc += d.pct;
    return `${d.color} ${from}% ${acc}%`;
  });
  return `conic-gradient(${stops.join(', ')})`;
})();

const VALUES = [
  { label: ['Customer', 'First'], pct: 92, color: '#34C062' },
  { label: ['Ownership'], pct: 78, color: '#4D8EF7' },
  { label: ['Kindness'], pct: 65, color: '#A992F7' },
  { label: ['Craft'], pct: 58, color: '#FF8A3D' },
];

const BulletList = ({ x, y, gap, color, items, size = 16.5 }) => items.map((t, i) => (
  <Abs key={t} u={u} x={x} y={y + i * gap - 11} style={{ display: 'flex', alignItems: 'center', gap: u(12), fontSize: u(size), color: '#2A2638' }}>
    <span style={{
      width: u(21), height: u(21), borderRadius: '50%', background: color, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon u={u} name="check" size={13} stroke={3.2} />
    </span>
    {t}
  </Abs>
));

export default function NativeVisual({ className }) {
  return (
    <Canvas W={W} H={H} className={className} label="BuddiesHR dashboard for HR admins, with employees working from Slack or Teams and data kept in sync between both">
      <Arrows W={W} H={H} arrows={[
        { d: 'M1084 128 C1050 130 1022 160 1028 188', color: '#F4A06B', head: [1085, 128, 0], tail: [1028, 190, 95] },
        { d: 'M72 945 C80 1010 180 1030 265 1003', color: '#F4A06B', tail: [72, 943, -95], head: [266, 1003, -15] },
        { d: 'M935 1003 C1010 1030 1090 1000 1118 948', color: '#9A86F2', tail: [933, 1003, 195], head: [1119, 946, -60] },
      ]} />

      <Sidebar u={u} x={25} y={53} w={97} h={874} items={['home', 'bot', 'calendar', 'bell', 'hash', 'plus']} active={0} itemTop={180} itemGap={73} />

      {/* Main dashboard */}
      <Card u={u} x={134} y={53} w={873} h={874} r={24}>
        <Abs u={u} x={36} y={26} style={{ fontSize: u(28), fontWeight: 700, letterSpacing: '-0.01em' }}>Dashboard</Abs>
        <Abs u={u} x={604} y={24} w={188} h={41} style={{
          display: 'flex', alignItems: 'center', gap: u(10), padding: `0 ${u(14)}`, boxSizing: 'border-box',
          border: `${u(1.5)} solid ${C.line}`, borderRadius: u(10), fontSize: u(14.5), fontWeight: 600,
        }}>
          <Icon u={u} name="calendar" size={17} color="#55516A" stroke={2} />
          May 20 – May 26
          <Icon u={u} name="chevronDown" size={13} color="#55516A" stroke={2.2} style={{ marginLeft: 'auto' }} />
        </Abs>
        <Abs u={u} x={805} y={20}><Avatar u={u} name="me" size={48} /></Abs>
      </Card>

      <Kpi x={166} w={218} label="Total Employees" value="512" delta="12%" up />
      <Kpi x={405} w={282} label="Time Off This Week" value="23" delta="8%" />
      <Kpi x={708} w={272} label="Open Requests" value="7" link="View all" />

      {/* Time off calendar */}
      <Panel x={166} y={258} w={340} h={330}>
        <Abs u={u} x={19} y={20}><Title>Time Off Calendar</Title></Abs>
        {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SUN'].map((d, i) => {
          const cx = [122, 159, 195, 231, 268, 304][i];
          const weekend = d === 'THU' || d === 'FRI' || d === 'SUN';
          return (
            <Abs key={d} u={u} x={cx - 18} y={54} w={36} style={{ textAlign: 'center', color: weekend && d !== 'SUN' ? '#E2603F' : '#55516A' }}>
              <div style={{ fontSize: u(9.5), fontWeight: 600, color: d === 'THU' || d === 'FRI' ? '#E2603F' : d === 'SUN' ? '#E2603F' : '#55516A' }}>{d}</div>
              <div style={{ fontSize: u(12.5), fontWeight: 600, marginTop: u(5), color: '#2A2638' }}>{20 + i}</div>
            </Abs>
          );
        })}
        {[140, 177, 213, 249, 286].map(x => (
          <Abs key={x} u={u} x={x} y={92} w={1.5} h={160} style={{ background: '#F2F0F6' }} />
        ))}
        {[['neha', 'Neha Sharma', 117], ['arjun', 'Arjun Patel', 169], ['priya', 'Priya Shah', 222]].map(([a, n, y]) => (
          <Abs key={a} u={u} x={13} y={y - 14} style={{ display: 'flex', alignItems: 'center', gap: u(10), fontSize: u(11.5), fontWeight: 600 }}>
            <Avatar u={u} name={a} size={28} /> {n}
          </Abs>
        ))}
        <Chip x={234} y={117} label="PTO" bg="#FFEBD6" color="#E8862C" />
        <Chip x={291} y={117} label="PTO" bg="#FFEBD6" color="#E8862C" />
        <Chip x={178} y={169} label="PTO" bg="#ECE8FD" color="#6A55D8" />
        <Chip x={235} y={221} w={42} label="WFH" bg="#E3F5E7" color="#2E9E4C" />
        <Abs u={u} x={17} y={298}><More>View full calendar</More></Abs>
      </Panel>

      {/* Requests */}
      <Panel x={522} y={258} w={222} h={330}>
        <Abs u={u} x={17} y={20}><Title>Requests</Title></Abs>
        {[
          ['neha', 'Neha Sharma', 'May 22 – May 24', 'Pending', 76],
          ['arjun', 'Arjun Patel', 'May 23', 'Pending', 135],
          ['rohan', 'Rohan Singh', 'May 27', 'Approved', 191],
        ].map(([a, n, d, s, y]) => (
          <Abs key={a} u={u} x={13} y={y - 15} w={196} style={{ display: 'flex', gap: u(10) }}>
            <Avatar u={u} name={a} size={30} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: u(11.5), fontWeight: 700, marginTop: u(1) }}>{n}</div>
              <div style={{ fontSize: u(11), color: C.mute, marginTop: u(5) }}>{d}</div>
            </div>
            <div style={{ marginTop: u(2) }}>
              {s === 'Pending'
                ? <Status label="Pending" bg="#FFF0DC" color="#E39130" />
                : <Status label="Approved" bg="#E3F5E7" color="#2E9E4C" />}
            </div>
          </Abs>
        ))}
        <Abs u={u} x={17} y={298}><More>View full requests</More></Abs>
      </Panel>

      {/* Headcount by department */}
      <Panel x={761} y={258} w={219} h={330}>
        <Abs u={u} x={17} y={20}><Title>Headcount by Department</Title></Abs>
        <Abs u={u} x={29} y={50} w={150} h={150} style={{ borderRadius: '50%', background: donutGradient }}>
          <Abs u={u} x={30} y={30} w={90} h={90} style={{
            borderRadius: '50%', background: '#fff',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{ fontSize: u(19), fontWeight: 700 }}>512</div>
            <div style={{ fontSize: u(11), fontWeight: 600, color: '#55516A', marginTop: u(2) }}>Total</div>
          </Abs>
        </Abs>
        {DEPTS.map((d, i) => (
          <Abs key={d.name} u={u} x={16} y={208 + i * 17.6} w={180} style={{ display: 'flex', alignItems: 'center', fontSize: u(10), color: '#2A2638' }}>
            <span style={{ width: u(7), height: u(7), borderRadius: '50%', background: d.color, marginRight: u(8) }} />
            {d.name}
            <span style={{ marginLeft: 'auto' }}>{d.value}</span>
          </Abs>
        ))}
        <Abs u={u} x={18} y={301}><More>View full report</More></Abs>
      </Panel>

      {/* Surveys running */}
      <Panel x={166} y={604} w={292} h={206}>
        <Abs u={u} x={14} y={18}><Title info>Surveys Running</Title></Abs>
        {[
          { icon: '🌿', bg: '#E2F6E6', title: 'Employee Engagement Survey', sub: 'Ends in 5 days', pct: '68%', fill: 156, color: '#2BB55A', y: 50 },
          { icon: '✨', bg: '#EEEBFD', title: 'New Joiner Experience Survey', sub: 'Ends in 12 days', pct: '42%', fill: 111, color: '#9C88F2', y: 110 },
        ].map(s => (
          <Abs key={s.title} u={u} x={13} y={s.y} w={266} h={50}>
            <Abs u={u} x={0} y={2} w={28} h={28} style={{ background: s.bg, borderRadius: u(6), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: u(15) }}>{s.icon}</Abs>
            <Abs u={u} x={41} y={0} style={{ fontSize: u(10.5), fontWeight: 700 }}>{s.title}</Abs>
            <Abs u={u} x={41} y={17} style={{ fontSize: u(9.5), color: C.mute }}>{s.sub}</Abs>
            <Abs u={u} x={190} y={0} w={72} style={{ textAlign: 'right', fontSize: u(12), fontWeight: 700 }}>{s.pct}</Abs>
            <Abs u={u} x={190} y={17} w={72} style={{ textAlign: 'right', fontSize: u(9), color: '#55516A' }}>Responses</Abs>
            <Abs u={u} x={41} y={34} w={225} h={5} style={{ background: '#EEEDF3', borderRadius: u(3) }}>
              <div style={{ width: u(s.fill), height: '100%', borderRadius: u(3), background: s.color }} />
            </Abs>
          </Abs>
        ))}
        <Abs u={u} x={16} y={180}><More>View all surveys</More></Abs>
      </Panel>

      {/* Company values usage */}
      <Panel x={475} y={604} w={281} h={206}>
        <Abs u={u} x={14} y={18}><Title info>Company Values Usage</Title></Abs>
        <Abs u={u} x={14} y={142} w={252} h={1.5} style={{ background: '#E6E4EC' }} />
        {VALUES.map((v, i) => {
          const cx = 38 + i * 69.5;
          const top = 66 + (92 - v.pct) * 0.6;
          return (
            <Abs key={i} u={u} x={cx - 34} y={0} w={68} h={206}>
              <Abs u={u} x={0} y={top - 16} w={68} style={{ textAlign: 'center', fontSize: u(10.5), fontWeight: 700 }}>{v.pct}%</Abs>
              <Abs u={u} x={18} y={top} w={32} h={142 - top} style={{ background: v.color, borderRadius: `${u(3)} ${u(3)} 0 0` }} />
              <Abs u={u} x={0} y={149} w={68} style={{ textAlign: 'center', fontSize: u(8.5), color: '#6C6880', lineHeight: 1.2 }}>
                {v.label.map(l => <div key={l}>{l}</div>)}
              </Abs>
            </Abs>
          );
        })}
        <Abs u={u} x={16} y={180}><More>View full report</More></Abs>
      </Panel>

      {/* Top kudos receivers */}
      <Panel x={771} y={604} w={209} h={206}>
        <Abs u={u} x={14} y={18}><Title info>Top Kudos Receivers</Title></Abs>
        {[['neha', 'Neha Sharma', '+120'], ['arjun', 'Arjun Patel', '+98'], ['priya', 'Priya Shah', '+76']].map(([a, n, p], i) => (
          <Abs key={a} u={u} x={11} y={53 + i * 41.5} w={190} style={{ display: 'flex', alignItems: 'center', fontSize: u(11), fontWeight: 600 }}>
            <span style={{
              width: u(18), height: u(18), borderRadius: '50%', border: `${u(1.5)} solid #DAD7E3`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: u(9.5), fontWeight: 700, marginRight: u(8), boxSizing: 'border-box',
            }}>{i + 1}</span>
            <Avatar u={u} name={a} size={26} style={{ marginRight: u(10) }} />
            {n}
            <span style={{ marginLeft: 'auto', color: C.blue, fontWeight: 700, fontSize: u(11.5) }}>{p}</span>
            <Icon u={u} name="star" size={16} color="#F7B62B" style={{ marginLeft: u(8) }} />
          </Abs>
        ))}
        <Abs u={u} x={15} y={182}><More>View all kudos</More></Abs>
      </Panel>

      {/* eNPS */}
      <Panel x={166} y={826} w={814} h={87}>
        <Abs u={u} x={15} y={10}><Title info>eNPS Score</Title></Abs>
        <Abs u={u} x={20} y={32} style={{ fontSize: u(30), fontWeight: 700 }}>+42</Abs>
        <Abs u={u} x={24} y={67} style={{ fontSize: u(12), fontWeight: 600, color: C.green }}>Excellent</Abs>
        <Abs u={u} x={131} y={24} w={1.5} h={46} style={{ background: '#E6E4EC' }} />
        <Abs u={u} x={152} y={31} style={{ fontSize: u(12.5), color: '#6C6880', lineHeight: 1.5 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: u(5) }}>
            <Icon u={u} name="arrowUp" size={13} color={C.green} stroke={2.4} />
            <span style={{ color: C.green }}>8</span> from
          </div>
          <div>last month</div>
        </Abs>
        <Abs u={u} x={277} y={47} w={514} h={5} style={{
          borderRadius: u(3),
          background: 'linear-gradient(90deg, #E5484D 0%, #F07A3A 22%, #F2C14E 38%, #D9D6E0 50%, #8FD18F 70%, #2BB55A 100%)',
        }} />
        <Abs u={u} x={727} y={43} w={13} h={13} style={{ borderRadius: '50%', background: '#2BB55A', border: `${u(2.5)} solid #fff`, boxShadow: `0 0 0 ${u(1)} #2BB55A`, boxSizing: 'border-box' }} />
        <Abs u={u} x={723} y={21}><Icon u={u} name="smile" size={20} color="#2BB55A" stroke={2.2} /></Abs>
        {[['-100', 286], ['0', 539], ['+100', 781]].map(([t, x]) => (
          <Abs key={t} u={u} x={x - 20} y={62} w={40} style={{ textAlign: 'center', fontSize: u(11), color: '#55516A' }}>{t}</Abs>
        ))}
      </Panel>

      {/* Employees live here */}
      <Card u={u} x={1095} y={58} w={331} h={414} r={22} bg="linear-gradient(160deg, #FFF6EF 0%, #FDEFE6 100%)" style={{ border: `${u(1.5)} solid #F7E3D6` }}>
        {['/img/redesign/slack-logo.png', '/img/redesign/teams-logo.png'].map((src, i) => (
          <Abs key={src} u={u} x={16 + i * 62} y={26} w={58} h={58} style={{
            background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 ${u(4)} ${u(12)} rgba(40, 30, 80, 0.10)`,
          }}>
            <img src={src} alt="" draggable="false" style={{ width: u(34), height: u(34) }} />
          </Abs>
        ))}
        <Abs u={u} x={151} y={44} style={{ fontSize: u(18.5), fontWeight: 700, color: C.coral, letterSpacing: '-0.01em' }}>Employees live here</Abs>
        <Abs u={u} x={24} y={100} w={290} style={{ fontSize: u(17), color: '#2A2638', lineHeight: 1.48 }}>
          Get work done in Slack or Teams<br />— no need to open the app.
        </Abs>
        <BulletList x={24} y={183} gap={37.2} color="#F07A3A" items={['Ask questions', 'Request time off', 'Check PTO balance', 'Get updates', 'Give feedback', 'And more…']} />
      </Card>

      {/* HR admins work here */}
      <Card u={u} x={1095} y={513} w={331} h={412} r={22} bg="linear-gradient(160deg, #F7F4FE 0%, #F1EDFC 100%)" style={{ border: `${u(1.5)} solid #E8E2FA` }}>
        <Abs u={u} x={18} y={22} w={68} h={68} style={{
          background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: `0 ${u(4)} ${u(12)} rgba(40, 30, 80, 0.10)`,
        }}>
          <Icon u={u} name="monitor" size={34} color={C.purple} stroke={2.2} />
        </Abs>
        <Abs u={u} x={105} y={43} style={{ fontSize: u(19), fontWeight: 700, color: '#5638E6', letterSpacing: '-0.01em' }}>HR admins work here</Abs>
        <Abs u={u} x={22} y={109} w={295} style={{ fontSize: u(17), color: '#2A2638', lineHeight: 1.55 }}>
          Use the web dashboard to<br />manage, analyze and take action.
        </Abs>
        <BulletList x={23} y={198} gap={40.5} color="#7B5CF5" items={['Dashboards & analytics', 'Org chart & headcount', 'Review cycles', 'Exportable reports', 'Templates & settings']} />
      </Card>

      {/* One source of truth */}
      <Abs u={u} x={286} y={945} w={627} h={113} style={{
        background: '#fff', borderRadius: u(57), border: `${u(1.5)} solid ${C.line}`, boxSizing: 'border-box',
        boxShadow: `0 ${u(12)} ${u(34)} rgba(40, 30, 80, 0.12)`,
      }}>
        <Abs u={u} x={24} y={15} w={82} h={82} style={{
          borderRadius: '50%', background: '#EFEBFC', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5B44D9',
        }}>
          <Icon u={u} name="link" size={40} stroke={2.4} />
        </Abs>
        <Abs u={u} x={145} y={27} style={{ fontSize: u(19.5), fontWeight: 700 }}>One source of truth</Abs>
        <Abs u={u} x={145} y={61} style={{ fontSize: u(16), color: '#55516A' }}>Data stays in sync across Slack/Teams and the platform.</Abs>
      </Abs>
    </Canvas>
  );
}
